import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { AccountSettings } from "../../store/actions/";
import whatsNewEntries from "./whatsNewEntries";
import { getUnseenWhatsNew, getVisibleWhatsNew } from "./_WhatsNew.utils";
// Types
import type { RootState } from "../../store/reducers";
import type { WhatsNewEntry } from "./_WhatsNew.types";

/**
 * selectWhatsNew(): What's new of the user logged in. Shared by the page, the
 * Home modal and the menu badge
 */
export const selectWhatsNew = ({
  accountSettings: { isWhatsNewLoaded, whatsNew },
  auth: { role, user },
}: RootState | any): {
  isLoaded: boolean;
  unseen: Array<WhatsNewEntry>;
  visible: Array<WhatsNewEntry>;
} => {
  const visible = getVisibleWhatsNew(whatsNewEntries, role || null);
  return {
    isLoaded: isWhatsNewLoaded,
    unseen: getUnseenWhatsNew(
      visible,
      (whatsNew && whatsNew.seenIds) || [],
      user && user.createdAt
    ),
    visible,
  };
};

export const useWhatsNew = () => {
  const dispatch = useDispatch();
  // Select the inputs, not the result: the result is a new object each time
  const accountSettings = useSelector(
    ({ accountSettings }: RootState | any) => accountSettings
  );
  const auth = useSelector(({ auth }: RootState | any) => auth);
  const { isLoaded, unseen, visible } = React.useMemo(
    () => selectWhatsNew({ accountSettings, auth }),
    [accountSettings, auth]
  );
  const onMarkAsSeen = React.useCallback(
    (entries: Array<WhatsNewEntry>) => {
      if (entries.length > 0) {
        dispatch(
          AccountSettings.accountSettingsUpdateWhatsNewSeen({
            ids: entries.map(({ id }) => id),
          })
        );
      }
    },
    [dispatch]
  );
  return { isLoaded, onMarkAsSeen, unseen, visible };
};
